import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, Users, FileSearch, History, Database, LogOut, Menu, X, ShieldCheck, UserPlus, Settings as SettingsIcon, Archive, Smartphone, Scale, ChevronDown, UserCog, Receipt, KeyRound } from "lucide-react";
import { api, clearSession } from "../lib/api";

const ADLIRAN_GROUP = {
  key: "adliran",
  label: "عدلیران",
  icon: Scale,
  children: [
    { key: "accounts", label: "مدیریت اکانت‌ها", icon: Users, path: "/accounts" },
    { key: "add", label: "افزودن اکانت", icon: UserPlus, path: "/accounts/add" },
    { key: "refresh_logs", label: "لاگ رفرش/لاگین", icon: History, path: "/refresh-logs" },
  ],
};

const NAV = [
  { key: "dashboard", label: "داشبورد آمار", icon: LayoutDashboard, path: "/" },
  ADLIRAN_GROUP,
  { key: "inquiries", label: "استعلامات اخیر", icon: FileSearch, path: "/inquiries" },
  { key: "identity_cache", label: "کش هویت‌ها", icon: Database, path: "/identity-cache" },
  { key: "mobile_cache", label: "کش شماره موبایل‌ها", icon: Smartphone, path: "/mobile-cache" },
  { key: "users", label: "کاربران و توکن‌ها", icon: UserCog, path: "/users" },
  { key: "invoices", label: "پیش‌فاکتورها", icon: Receipt, path: "/invoices" },
  { key: "portal_settings", label: "پیامک و پیش‌فاکتور", icon: KeyRound, path: "/portal-settings" },
  { key: "backup", label: "بکاپ و ریستور", icon: Archive, path: "/backup" },
  { key: "settings", label: "تنظیمات", icon: SettingsIcon, path: "/settings" },
];

function NavGroup({ group, onNavigate }) {
  const loc = useLocation();
  const insideGroup = group.children.some((c) => loc.pathname.startsWith(c.path));
  const [open, setOpen] = useState(insideGroup);
  const Icon = group.icon;
  return (
    <div data-testid={`nav-group-${group.key}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
          insideGroup ? "text-teal-700" : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        <Icon size={18} />
        <span className="flex-1 text-right">{group.label}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mr-4 mt-1 space-y-1 border-r border-slate-200 pr-2">
          {group.children.map((c) => {
            const Icon2 = c.icon;
            return (
              <NavLink
                key={c.key}
                to={c.path}
                data-testid={`nav-${c.key}`}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    isActive ? "bg-teal-50 text-teal-700" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <Icon2 size={16} />
                <span>{c.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSession();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50/70 flex" dir="rtl">
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 right-0 h-screen w-64 bg-white border-l border-slate-200/80 shadow-sm z-40 py-5 px-4 flex flex-col justify-between transition-transform ${mobileOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}`}
      >
        <div>
          <div className="flex items-center gap-3 px-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 grid place-items-center text-white shadow-md">
              <ShieldCheck size={22} />
            </div>
            <div>
              <div className="font-bold text-slate-900 leading-tight">سامانه استعلام هویت</div>
              <div className="text-xs text-slate-500">پنل مدیریت</div>
            </div>
          </div>

          <nav className="space-y-1">
            {NAV.map((n) => {
              if (n.children) {
                return <NavGroup key={n.key} group={n} onNavigate={() => setMobileOpen(false)} />;
              }
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.key}
                  to={n.path}
                  end={n.path === "/"}
                  data-testid={`nav-${n.key}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? "bg-teal-50 text-teal-700 shadow-sm" : "text-slate-600 hover:bg-slate-100"}`
                  }
                >
                  <Icon size={18} />
                  <span>{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition-all"
        >
          <LogOut size={18} />
          <span>خروج از پنل</span>
        </button>
      </aside>

      <button
        className="fixed top-4 left-4 lg:hidden z-50 bg-white shadow-md rounded-xl p-2"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <main className="flex-1 min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 px-6 flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">پنل مدیریت</div>
            <div className="text-base font-semibold text-slate-800">سامانه استعلام هویت</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200/60 rounded-full px-2.5 py-1 font-semibold">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              سرویس آنلاین
            </span>
          </div>
        </header>

        <AnimatePresence mode="wait">
          <motion.div
            key={typeof window !== "undefined" ? window.location.pathname : "p"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="p-4 md:p-8"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
